import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import omnicareLogo from '../assets/icons/logo.svg';
import {
  CheckCircle2, ChevronDown, ChevronUp, Share2,
  ShieldCheck, BookOpen, ArrowRight, Activity,
  Heart, Download, FileText,
  Sparkles, Camera, Users, X, Twitter,
  Loader2, Image as ImageIcon
} from 'lucide-react';

// ─── Utility loaders ───
const loadHtml2Canvas = () => new Promise((resolve, reject) => {
  if (window.html2canvas) { resolve(window.html2canvas); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
  s.onload = () => resolve(window.html2canvas);
  s.onerror = () => reject(new Error('Failed to load html2canvas'));
  document.body.appendChild(s);
});

const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf) { resolve(window.jspdf.jsPDF); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  s.onload = () => resolve(window.jspdf.jsPDF);
  s.onerror = () => reject(new Error('Failed to load jsPDF'));
  document.body.appendChild(s);
});

// ─── Cancer copy ───
const CANCER_COPY = {
  lung: { elevated: "A low-dose CT scan is quick, painless, and catches issues when they're most treatable.", routine: "No immediate screening needed. If you ever become a long-term smoker, ask about low-dose CT.", action: { elevated: "Ask about a low-dose CT scan", routine: "Learn about lung screening" } },
  breast: { elevated: "Mammograms are one of the most effective screening tools — and they only take about 20 minutes.", routine: "Regular mammograms at the recommended age are simple, fast, and genuinely lifesaving.", action: { elevated: "Schedule a mammogram", routine: "Check your mammogram timeline" } },
  cervical: { elevated: "A Pap test with HPV co-testing is the gold standard — one of the most preventable cancers when caught early.", routine: "Great news: cervical cancer is one of the most preventable cancers. Stay on top of your routine Pap + HPV tests.", action: { elevated: "Book your Pap + HPV test", routine: "Confirm you're up to date" } },
  colorectal: { elevated: "Multiple options available — from simple at-home stool tests to colonoscopy. Talk to your doctor about what fits you.", routine: "Screening starts at age 45 for most people. Several options from at-home kits to colonoscopy.", action: { elevated: "Discuss early screening options", routine: "Plan your first screening" } },
  gastric: { elevated: "Proactive screening could make a real difference. An upper endoscopy is the most common first step.", routine: "If you develop persistent digestive symptoms or a family history surfaces, bring it up with your doctor.", action: { elevated: "Ask about an endoscopy", routine: "Know the warning signs" } },
  liver: { elevated: "Regular monitoring with ultrasound and blood tests (AFP) can catch changes early. Especially important with hepatitis B/C.", routine: "Healthy weight, limited alcohol, and up-to-date hepatitis vaccines are your best preventive steps.", action: { elevated: "Ask about liver monitoring", routine: "Review your hepatitis status" } },
  thyroid: { elevated: "Highly treatable — a simple neck exam and ultrasound can catch most issues early.", routine: "Routine physical exams with a neck check are usually sufficient. No special screening needed.", action: { elevated: "Request a thyroid check", routine: "Stay aware during checkups" } },
  skin: { elevated: "Annual full-body skin exams plus regular self-checks are your best defense. Most skin cancers are completely curable when caught early.", routine: "Wear sunscreen, do monthly self-checks for new or changing moles, and see a dermatologist if anything looks off.", action: { elevated: "Book a skin exam", routine: "Start monthly self-checks" } },
  prostate: { elevated: "A simple PSA blood test is the first step — quick and straightforward.", routine: "The PSA test is standard; talk to your doctor about when to start based on your age.", action: { elevated: "Ask about a PSA test", routine: "Plan your PSA timeline" } },
  ovarian: { elevated: "No perfect screening test yet, but CA-125 blood tests and transvaginal ultrasound can help — especially with family history.", routine: "No routine screening for average risk. Report any persistent bloating, pelvic pain, or changes to your doctor.", action: { elevated: "Discuss monitoring options", routine: "Know the symptoms" } },
  uterine: { elevated: "Report any unexpected vaginal bleeding promptly — early detection makes a huge difference.", routine: "Stay attentive to any unusual bleeding and keep up with regular gynecological exams.", action: { elevated: "Talk to your gynecologist", routine: "Stay up to date on exams" } },
  testicular: { elevated: "One of the most treatable cancers when caught early. Monthly self-exams take less than a minute.", routine: "Self-exams are simple and effective — highest cure rate when detected early.", action: { elevated: "Learn the self-exam technique", routine: "Start monthly self-exams" } },
};
const DEFAULT_COPY = { elevated: "Early detection is one of the most powerful tools in cancer prevention. Discuss screening with your doctor.", routine: "Staying on top of routine screenings is the best way to keep things on track.", action: { elevated: "Talk to your doctor", routine: "Plan your screening" } };

// ─── Helper functions (copied from ResultPageV2) ───
function mapRecommendationsToCards(recommendations, surveyData) {
  const { stronglyRecommend = [], recommend = [], belowAverage = [] } = recommendations || {};
  const allItems = [...stronglyRecommend, ...recommend];
  const iconMap = { gastric: Activity, liver: Activity, lung: ShieldCheck, breast: Heart, cervical: ShieldCheck, colorectal: Activity, thyroid: ShieldCheck, skin: ShieldCheck, prostate: ShieldCheck, ovarian: Heart, uterine: Heart, testicular: ShieldCheck };
  return allItems.map((item) => {
    const score = item.riskScore || 1.0;
    const isElevated = score > 1.2;
    let statusColor = 'green', priority = 'On Your Radar';
    if (score >= 2.5) { statusColor = 'red'; priority = 'Act Now'; }
    else if (score >= 1.5) { statusColor = 'orange'; priority = 'Worth a Conversation'; }
    else if (score > 1.2) { statusColor = 'stone'; priority = 'Good to Know'; }
    const riskFactors = item.riskFactors || [];
    const screening = item.screening || {};
    const copy = CANCER_COPY[item.cancerKey] || DEFAULT_COPY;
    const sources = [];
    if (item.guidelineSource?.org) sources.push(`${item.guidelineSource.org}${item.guidelineSource.note ? ': ' + item.guidelineSource.note : ''}`);
    if (screening.source) sources.push(screening.source);
    if (screening.guideline && !sources.length) sources.push(screening.guideline);
    const clinicalParts = [];
    if (riskFactors.length > 0) { clinicalParts.push("What we found in your profile:"); riskFactors.forEach(r => { const factor = r.factor || r; const detail = r.detail || ''; clinicalParts.push(`• ${factor}${detail ? ' — ' + detail : ''}`); }); }
    if (screening.method) { clinicalParts.push(''); clinicalParts.push(`Recommended screening: ${screening.method}`); if (screening.interval) clinicalParts.push(`How often: ${screening.interval}`); if (screening.who) clinicalParts.push(`Who should get it: ${screening.who}`); if (screening.guideline) clinicalParts.push(`Guideline: ${screening.guideline}`); }
    if (screening.highRisk) { clinicalParts.push(''); clinicalParts.push(`For higher-risk individuals: ${screening.highRisk}`); }
    if (item.statistics?.length > 0) { clinicalParts.push(''); clinicalParts.push('By the numbers:'); item.statistics.forEach(s => clinicalParts.push(`• ${s.label}: ${s.value}`)); }
    return { title: item.cancer || 'Screening', priority, multiplier: score.toFixed(1), statusColor, icon: iconMap[item.cancerKey] || ShieldCheck, summary: isElevated ? copy.elevated : copy.routine, action: isElevated ? copy.action.elevated : copy.action.routine, clinicalText: clinicalParts.join('\n'), sources, isGovRecommended: item.tier === 'primary', guidelineSource: item.guidelineSource || null };
  });
}

function getArchetype(surveyData) {
  if (!surveyData) return { name: "The Seedling", emoji: "🌱", superpower: "Taking the first step", watchOut: "The unknown", percentile: "Top 60%" };
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits, familyCancerHistory = [], livedWithSmoker } = surveyData;
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  const isHealthyBmi = bmi && bmi >= 18.5 && bmi < 25;
  const isNonSmoker = (!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  const isLowAlcohol = !alcoholConsumption || alcoholConsumption === "none" || alcoholConsumption === "under_7";
  const hasGoodDiet = !dietHabits || dietHabits === 'no' || dietHabits === 'unsure';
  const noSecondhandSmoke = livedWithSmoker !== 'yes';
  const healthScore = [isHealthyBmi, isNonSmoker, isLowAlcohol, hasGoodDiet, noSecondhandSmoke].filter(Boolean).length;
  const isFormerSmoker = (smokingHistory > 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  const filteredFamily = (Array.isArray(familyCancerHistory) ? familyCancerHistory : []).filter(fh => fh.toLowerCase() !== 'none');
  const hasFamilyHistory = filteredFamily.length > 0;
  const alcoholIsWeakSpot = !isLowAlcohol;
  const dietIsWeakSpot = !hasGoodDiet;
  const weakSpot = !isNonSmoker ? "Smoking exposure" : !isLowAlcohol ? "Alcohol intake" : !hasGoodDiet ? "Processed food habits" : !isHealthyBmi ? "Weight management" : !noSecondhandSmoke ? "Secondhand smoke exposure" : hasFamilyHistory ? "Family history — not your fault, but worth screening for" : "Complacency — don't skip your checkups!";
  if (isFormerSmoker && healthScore >= 3) return { name: "The Phoenix", emoji: "🔥", superpower: "You made the hardest change already", watchOut: weakSpot, percentile: "Top 15%", tagline: "Rose from the ashes" };
  if (healthScore === 5 && !hasFamilyHistory) return { name: "The Zen Master", emoji: "🧘", superpower: "Balance in all things", watchOut: weakSpot, percentile: "Top 8%", tagline: "Calm, in control" };
  if (healthScore === 5 && hasFamilyHistory) return { name: "The Sentinel", emoji: "🛡️", superpower: "Proactive despite the odds", watchOut: weakSpot, percentile: "Top 10%", tagline: "Guarding against what you can't control" };
  if (healthScore === 4) return { name: "The Adventurer", emoji: "🧭", superpower: "Life's for living — and you're mostly nailing it", watchOut: weakSpot, percentile: "Top 20%", tagline: "Mostly solid, one indulgence" };
  if (healthScore >= 2) {
    if (alcoholIsWeakSpot || dietIsWeakSpot) return { name: "The Rebel", emoji: "🎸", superpower: "You know the risks and you own them", watchOut: weakSpot, percentile: "Top 35%", tagline: "Lives a little, knows the score" };
    return { name: "The Night Owl", emoji: "🦉", superpower: "Self-aware and honest about where you stand", watchOut: weakSpot, percentile: "Top 40%", tagline: "Eyes wide open" };
  }
  return { name: "The Seedling", emoji: "🌱", superpower: "The hardest step is the first one — you just took it", watchOut: weakSpot, percentile: "Top 60%", tagline: "Everything ahead of you" };
}

function getWins(surveyData) {
  if (!surveyData) return [];
  const wins = [];
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits } = surveyData;
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  if (bmi && bmi >= 18.5 && bmi < 25) wins.push({ title: `Healthy BMI (${bmi.toFixed(1)})`, desc: "A healthy weight lowers your risk for at least 13 types of cancer." });
  if ((!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0)) wins.push({ title: "Smoke-Free", desc: "Being smoke-free is the single most protective thing you can do." });
  if (dietHabits === 'no') wins.push({ title: "Whole-Food Diet", desc: "Avoiding processed foods is linked to lower risk of colorectal, gastric, and liver cancers." });
  if (!alcoholConsumption || alcoholConsumption === "none") wins.push({ title: "Low Alcohol Intake", desc: "Keeping it minimal puts you ahead of the curve." });
  if (wins.length === 0) wins.push({ title: "You Showed Up", desc: "Taking this quiz means you're thinking about prevention." });
  return wins.slice(0, 3);
}

// ─── PDF Generation (same as V2) ───
async function generatePDF(surveyData, recommendations) {
  const jsPDF = await loadJsPDF();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = margin;
  const checkPage = (needed = 20) => { if (y + needed > pageH - 20) { doc.addPage(); y = margin; } };
  const drawLine = () => { doc.setDrawColor(200); doc.line(margin, y, pageW - margin, y); y += 4; };
  doc.setFillColor(68, 98, 74);
  doc.rect(0, 0, pageW, 40, 'F');
  doc.setTextColor(255); doc.setFontSize(22); doc.setFont('helvetica', 'bold');
  doc.text('Pebble Health', margin, 18);
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text('Personalized Cancer Screening Report', margin, 27);
  doc.setFontSize(9);
  doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, 34);
  y = 50;
  doc.setTextColor(30); doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text('Patient Profile', margin, y); y += 8;
  doc.setFontSize(10); doc.setFont('helvetica', 'normal');
  const bmi = (surveyData.weightInKg && surveyData.heightInCm) ? (surveyData.weightInKg / ((surveyData.heightInCm / 100) ** 2)).toFixed(1) : 'N/A';
  const profileItems = [['Age', surveyData.age || 'N/A'], ['Biological Sex', surveyData.biologicalSex || 'N/A'], ['Ethnicity', surveyData.ethnicity || 'N/A'], ['BMI', `${bmi} (${surveyData.weightInKg}kg, ${surveyData.heightInCm}cm)`], ['Smoking', surveyData.smokingHistory > 0 ? `${surveyData.smokingHistory} years` : 'Non-smoker'], ['Alcohol', surveyData.alcoholConsumption || 'None']];
  const colW = contentW / 2;
  profileItems.forEach((item, i) => { const col = i % 2; if (col === 0 && i > 0) y += 6; checkPage(8); const x = margin + col * colW; doc.setFont('helvetica', 'bold'); doc.setTextColor(100); doc.setFontSize(8); doc.text(item[0].toUpperCase(), x, y); doc.setFont('helvetica', 'normal'); doc.setTextColor(30); doc.setFontSize(10); doc.text(String(item[1]), x, y + 4); });
  y += 18; drawLine();
  const { stronglyRecommend = [], recommend = [], belowAverage = [] } = recommendations || {};
  const renderSection = (title, items, color) => { if (items.length === 0) return; checkPage(30); doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(...color); doc.text(title, margin, y); y += 10; items.forEach((item) => { checkPage(20); doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(30); doc.text(item.cancer || 'Screening', margin + 4, y); y += 6; if (item.description) { doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(80); const lines = doc.splitTextToSize(item.description, contentW - 8); doc.text(lines, margin + 4, y); y += lines.length * 4 + 4; } y += 4; drawLine(); }); };
  renderSection('Strongly Recommended', stronglyRecommend, [185, 28, 28]);
  renderSection('Recommended', recommend, [180, 120, 20]);
  renderSection('Routine Monitoring', belowAverage, [80, 120, 80]);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); doc.setFontSize(7); doc.setTextColor(160); doc.text('Pebble Health — pebblehealth.ai', margin, pageH - 10); doc.text(`Page ${i} of ${totalPages}`, pageW - margin - 20, pageH - 10); }
  doc.save('pebble-health-screening-report.pdf');
}

// ─── Share Modal ───
const ShareModal = ({ isOpen, onClose, imageUrl }) => {
  if (!isOpen) return null;
  const handleDownload = () => { const link = document.createElement('a'); link.download = 'pebblehealth-archetype.png'; link.href = imageUrl; link.click(); };
  const handleTwitterShare = () => { const text = encodeURIComponent("Just got my proactive health blueprint from Pebble Health ✨"); const url = encodeURIComponent("https://pebblehealth.ai"); window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank'); };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2"><Sparkles size={18} /> Share Your Result</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-6 flex flex-col items-center gap-4">
          {imageUrl ? <img src={imageUrl} alt="Archetype" className="w-64 rounded-xl shadow-lg" /> : <div className="w-64 aspect-[4/5] bg-gray-200 animate-pulse rounded-xl" />}
          <button onClick={handleDownload} className="w-full py-3 bg-[#44624a] text-white rounded-xl font-bold flex items-center justify-center gap-2"><ImageIcon size={18} /> Save Image</button>
          <button onClick={handleTwitterShare} className="w-full py-3 bg-black text-white rounded-xl font-bold flex items-center justify-center gap-2"><Twitter size={18} /> Share to X</button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ResultPageV3 — "Magazine Editorial"
// ═══════════════════════════════════════════════════════════════
export default function ResultPageV3() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const carouselRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  const analysisResults = useMemo(() => {
    try { const stored = localStorage.getItem("omnicare_analysis_results"); return stored ? JSON.parse(stored) : null; } catch { return null; }
  }, []);
  const { surveyData, recommendations } = analysisResults || {};
  const prioritiesData = useMemo(() => {
    const cards = mapRecommendationsToCards(recommendations, surveyData);
    return cards.sort((a, b) => { if (a.isGovRecommended && !b.isGovRecommended) return -1; if (b.isGovRecommended && !a.isGovRecommended) return 1; return parseFloat(b.multiplier) - parseFloat(a.multiplier); });
  }, [recommendations, surveyData]);
  const archetype = useMemo(() => getArchetype(surveyData), [surveyData]);
  const wins = useMemo(() => getWins(surveyData), [surveyData]);

  const handleCaptureCard = async () => {
    if (!cardRef.current) return;
    setIsCapturing(true);
    try {
      const html2canvas = await loadHtml2Canvas();
      const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, backgroundColor: '#064e3b', logging: false });
      const imgDataUrl = canvas.toDataURL('image/png');
      setCapturedImage(imgDataUrl);
      setShareModalOpen(true);
    } catch (e) { console.error(e); }
    finally { setIsCapturing(false); }
  };

  const scrollCarousel = (dir) => {
    if (carouselRef.current) carouselRef.current.scrollBy({ left: dir * 400, behavior: 'smooth' });
  };

  if (!analysisResults) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="font-serif text-2xl font-bold">No results found</h2>
          <p className="text-stone-600">Complete the health screening quiz first.</p>
          <button onClick={() => navigate('/survey/1')} className="px-6 py-3 bg-[#44624a] text-white rounded-xl font-bold">Take the Quiz</button>
        </div>
      </div>
    );
  }

  const summaryText = prioritiesData.some(p => p.statusColor === 'red')
    ? "We found a few things worth acting on soon. Nothing to panic about — but having a conversation with your doctor this year could make a real difference."
    : prioritiesData.some(p => p.statusColor === 'orange')
    ? "Your lifestyle looks solid overall. We've flagged a couple of areas where early screening could give you extra peace of mind."
    : "Your profile looks great. We've mapped out a simple screening timeline to help you stay ahead — no surprises, just a plan.";

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#2C2825] selection:bg-[#d4c5a9]/40">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FAF8F5]/90 backdrop-blur-md border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/landing')}>
            <img src={omnicareLogo} alt="Pebble Health" className="w-7 h-7 opacity-90" />
            <span className="text-lg font-semibold tracking-tight">Pebble Health</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/results')} className="hidden sm:block text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors">Classic View</button>
            <button onClick={handleCaptureCard} className="flex items-center gap-2 text-sm font-semibold bg-[#E8E2D9] hover:bg-[#DDD6CB] px-4 py-2 rounded-full transition-colors text-[#5A4E3C]">
              <Share2 size={14} /> Share
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO: Full-width with massive typography ── */}
      <section className="relative overflow-hidden bg-[#F5F0E8] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-32 flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 space-y-6">
            <p className="uppercase tracking-[0.3em] text-sm font-semibold text-[#8B7D6B]">Your Health Archetype</p>
            <h1 className="font-serif leading-[0.85] tracking-tight" style={{ fontSize: 'clamp(72px, 12vw, 140px)', color: '#2C2825' }}>
              {archetype.name.replace('The ', '')}
            </h1>
            <p className="text-2xl text-[#8B7D6B] font-light max-w-md" style={{ fontFamily: 'Georgia, serif' }}>
              {archetype.tagline || archetype.superpower}
            </p>
            <div className="flex items-center gap-3 pt-4">
              <span className="text-5xl">{archetype.emoji}</span>
              <span className="text-sm text-[#8B7D6B] font-medium">{archetype.percentile} of users</span>
            </div>
          </div>
          {/* Archetype card (capturable) */}
          <div ref={cardRef} className="w-80 aspect-[4/5] bg-gradient-to-br from-[#44624a] via-[#3a5440] to-[#2f4536] rounded-2xl p-8 text-white shadow-2xl flex flex-col justify-between flex-shrink-0">
            <div className="flex justify-between items-start">
              <span className="text-xs uppercase tracking-widest font-bold text-white/60">Health Profile</span>
              <span className="text-3xl">{archetype.emoji}</span>
            </div>
            <div>
              <h2 className="font-serif text-4xl font-bold leading-tight">{archetype.name}</h2>
              {archetype.tagline && <p className="text-white/40 text-sm italic mt-1">{archetype.tagline}</p>}
              <p className="text-white/60 text-sm mt-2"><Users size={14} className="inline mr-1" />{archetype.percentile}</p>
            </div>
            <div className="space-y-2">
              <div className="bg-white/10 rounded-lg p-3 border border-white/10">
                <p className="text-[10px] text-[#a3c9a8] uppercase tracking-wider font-bold">Strength</p>
                <p className="text-sm font-semibold">{archetype.superpower}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 border border-white/10">
                <p className="text-[10px] text-orange-300 uppercase tracking-wider font-bold">Watch Out</p>
                <p className="text-sm font-semibold">{archetype.watchOut}</p>
              </div>
            </div>
            <div className="pt-4 border-t border-white/10 flex items-center gap-2">
              <div className="w-5 h-5 bg-white rounded flex items-center justify-center"><Activity className="text-[#2f4536]" size={12} /></div>
              <span className="font-serif font-bold text-xs text-white/70">Pebble Health</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── PULL QUOTE: personalized summary ── */}
      <section className="max-w-4xl mx-auto px-6 py-20 md:py-28">
        <div className="border-l-4 border-[#C4B79A] pl-8 md:pl-12">
          <p className="font-serif text-2xl md:text-4xl leading-relaxed text-[#3D3630] font-light italic">
            "{summaryText}"
          </p>
          <p className="mt-6 text-sm uppercase tracking-widest text-[#8B7D6B] font-semibold">— Your Pebble Health Report</p>
        </div>
      </section>

      {/* ── WINS: Horizontal ribbon ── */}
      {wins.length > 0 && (
        <section className="bg-[#44624a] text-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center gap-6">
            <h3 className="font-serif text-xl font-bold whitespace-nowrap flex items-center gap-2">
              <CheckCircle2 size={22} /> What's Working
            </h3>
            <div className="w-px h-8 bg-white/20 hidden md:block" />
            <div className="flex flex-wrap md:flex-nowrap gap-6">
              {wins.map((win, i) => (
                <div key={i} className="flex items-start gap-3 min-w-[200px]">
                  <div className="w-2 h-2 rounded-full bg-[#a3c9a8] mt-2 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-sm">{win.title}</p>
                    <p className="text-white/60 text-xs mt-0.5">{win.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Screening Priorities: Magazine column layout ── */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end gap-4 mb-10">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B7D6B] mb-1">Screening Priorities</p>
            <h2 className="font-serif text-3xl font-bold text-[#2C2825]">Your Action Items</h2>
          </div>
          <div className="h-px bg-[#E8E2D9] flex-1" />
        </div>

        {prioritiesData.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {prioritiesData.map((data, index) => {
              const isExpanded = expandedCard === index;
              const dotColors = { red: 'bg-red-500', orange: 'bg-amber-500', stone: 'bg-stone-400', green: 'bg-emerald-500' };
              const leftBorder = { red: 'border-l-red-500', orange: 'border-l-amber-500', stone: 'border-l-stone-400', green: 'border-l-emerald-500' };
              return (
                <div key={index} className={`bg-white border border-[#E8E2D9] border-l-[3px] ${leftBorder[data.statusColor]} rounded-lg overflow-hidden hover:shadow-md transition-shadow`}>
                  {/* Compact header row */}
                  <button onClick={() => setExpandedCard(isExpanded ? null : index)} className="w-full text-left px-4 py-3 flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColors[data.statusColor]}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-serif text-sm font-bold text-[#2C2825] truncate">{data.title}</h3>
                        <span className="text-[10px] font-mono font-bold text-[#8B7D6B] flex-shrink-0">{data.multiplier}×</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-[#8B7D6B] uppercase tracking-wider">{data.priority}</span>
                        {data.isGovRecommended && <span className="text-[8px] font-bold text-blue-600 bg-blue-50 px-1 py-px rounded">GOV</span>}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp size={14} className="text-[#C4B79A] flex-shrink-0" /> : <ChevronDown size={14} className="text-[#C4B79A] flex-shrink-0" />}
                  </button>
                  
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-[#F0ECE4]">
                      <p className="text-[13px] text-[#5A4E3C] leading-relaxed mt-3" style={{ fontFamily: 'Georgia, serif' }}>{data.summary}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#44624a] mt-2">
                        {data.action} <ArrowRight size={12} />
                      </span>
                      {data.clinicalText && (
                        <details className="mt-3">
                          <summary className="text-[11px] font-semibold text-[#8B7D6B] cursor-pointer flex items-center gap-1"><BookOpen size={12} /> The science</summary>
                          <div className="mt-2 p-3 bg-[#FAF8F5] rounded-lg text-[11px] text-[#5A4E3C] whitespace-pre-line border border-[#E8E2D9]">
                            {data.clinicalText}
                            {data.sources?.filter(Boolean).length > 0 && (
                              <div className="mt-2 pt-2 border-t border-[#E8E2D9]">
                                {data.sources.filter(Boolean).map((s, i) => <p key={i} className="text-[10px] text-[#8B7D6B]">[{i + 1}] {s}</p>)}
                              </div>
                            )}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white border border-[#E8E2D9] rounded-lg p-8 text-center">
            <ShieldCheck className="text-[#44624a] mx-auto mb-3" size={36} />
            <h3 className="font-serif text-xl font-bold mb-1">All Clear!</h3>
            <p className="text-[#8B7D6B] text-sm">No elevated risk factors detected.</p>
          </div>
        )}
      </section>

      {/* ── PDF Download ── */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <div className="bg-[#2C2825] rounded-2xl p-10 md:p-16 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 opacity-5 transform translate-x-1/4 -translate-y-1/4"><FileText size={300} /></div>
          <div className="relative z-10 space-y-6">
            <p className="uppercase tracking-[0.3em] text-sm font-semibold text-[#C4B79A]">Full Report</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold">The complete clinical breakdown</h2>
            <p className="text-white/60 text-lg max-w-xl mx-auto" style={{ fontFamily: 'Georgia, serif' }}>
              Download a professional PDF to bring to your next doctor's appointment.
            </p>
            <button onClick={() => generatePDF(surveyData, recommendations)} className="mt-4 px-8 py-4 bg-white text-[#2C2825] rounded-xl font-bold flex items-center justify-center gap-2 mx-auto hover:bg-stone-100 transition-colors">
              <Download size={20} /> Download PDF Report
            </button>
          </div>
        </div>
      </section>

      {/* Mobile classic view link */}
      <div className="sm:hidden text-center pb-8">
        <button onClick={() => navigate('/results')} className="text-[#8B7D6B] underline text-sm">View Classic Results Page →</button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} imageUrl={capturedImage} />

      {/* Hide scrollbar */}
      <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
}
